import { Container, Sprite, type Texture } from 'pixi.js';
import { VIRTUAL_HEIGHT, VIRTUAL_WIDTH } from '@/core/viewport';
import { ITEMS } from '@/data/items';
import { TILE_SIZE } from '@/sprites/tiles';
import type { GroundItem } from '@/world/groundItems';

/**
 * Dropped piles lying in the world.
 *
 * One shape tinted per item, pooled and culled to the view like every other
 * renderer here — a floor strewn with a season's rejected turnips should cost
 * the same as an empty one.
 */
export class GroundItemsRenderer {
  readonly container = new Container();
  private readonly pool: Sprite[] = [];

  constructor(private readonly texture: Texture) {}

  render(items: readonly GroundItem[], cameraX: number, cameraY: number): void {
    let used = 0;

    for (const item of items) {
      const x = item.x * TILE_SIZE;
      const y = item.y * TILE_SIZE;
      if (x < cameraX - TILE_SIZE || x > cameraX + VIRTUAL_WIDTH) continue;
      if (y < cameraY - TILE_SIZE || y > cameraY + VIRTUAL_HEIGHT) continue;

      const sprite = this.spriteAt(used++);
      sprite.texture = this.texture;
      sprite.tint = ITEMS[item.id].tint;
      sprite.position.set(x, y);
      sprite.visible = true;
    }

    for (let i = used; i < this.pool.length; i++) this.pool[i]!.visible = false;
  }

  private spriteAt(index: number): Sprite {
    let sprite = this.pool[index];
    if (!sprite) {
      sprite = new Sprite(this.texture);
      this.pool.push(sprite);
      this.container.addChild(sprite);
    }
    return sprite;
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }
}
